import React, { useCallback, useEffect, useRef } from 'react';

import GGApi from '../../../utils/GGApi';
import MasterButton from '../../general_components/MasterButton/MasterButton.js';
import {
    ACH_MANDATE_TEXT,
    cleanupFcOauthStorage,
    FC_OAUTH_HANDOFF_KEY,
    FC_OAUTH_MESSAGE_TYPE,
    finishInstantBankAdd,
    prepareFcOauthStorage,
    readStoredFcClientSecret,
    readStoredFcSetupIntentId,
} from './instantBankShared';

const FC_POST_COLLECT_STATUSES = ['requires_confirmation', 'requires_action', 'succeeded', 'processing'];

/**
 * Instant bank collection via Stripe Financial Connections.
 */
export default function InstantBankFc(props) {
    const {
        stripe,
        verification,
        setParentState,
        save_gift_for_oauth,
        cleanupFcOauth,
        bankNext,
        fc_client_secret,
        oauthResumeOnly,
    } = props;

    const resumeStarted = useRef(false);
    const collectInFlightRef = useRef(false);
    const handoffDebounceRef = useRef(false);
    const handoffFallbackTimerRef = useRef(null);
    const runOauthResumeFromStorageRef = useRef(null);

    const clearHandoffFallbackTimer = useCallback(() => {
        if (handoffFallbackTimerRef.current) {
            clearTimeout(handoffFallbackTimerRef.current);
            handoffFallbackTimerRef.current = null;
        }
    }, []);
    const runFcCollection = useCallback(async (clientSecret, setupIntentId, options = {}) => {
        const { fromOauthResume = false } = options;

        if (!stripe) {
            // Fix A: OAuth resume waits for stripe; only alert on explicit user click.
            if (!oauthResumeOnly && !fromOauthResume) {
                alert('Payment system is still loading. Please try again in a moment.');
            }
            setParentState({ loading: false });
            return;
        }

        setParentState({ loading: true });

        // set up variable for the start payload
        let startPayload = null;
        // if the client secret (needed for financial connections) is not provided, we need to get the client secret from the server
        if (!clientSecret) {
            // get the client secret from the server
            startPayload = await GGApi.bank_fc_start();
            // couldnt get the client secret from the server, cant continue
            if (!startPayload.success) {
                alert(startPayload.message || 'Could not start bank verification.');
                setParentState({ loading: false });
                return;
            }

            // got the client secret from the server, set the client secret and setup intent id
            clientSecret = startPayload.client_secret;
            setupIntentId = startPayload.setup_intent_id;
            // prepare the oauth storage for the financial connections flow
            prepareFcOauthStorage(save_gift_for_oauth, clientSecret, setupIntentId);
        }

        const holderName = (startPayload && startPayload.holder_name)
            || (() => {
                const user = JSON.parse(localStorage.getItem('user') || 'null');
                return user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : '';
            })();
        const email = (startPayload && startPayload.email)
            || (() => {
                const user = JSON.parse(localStorage.getItem('user') || 'null');
                return user && user.email ? user.email : '';
            })();

            // set up is done, now we can open the UI to collect the bank account
        try {
            // --- Collect bank account via Stripe Financial Connections ---
            collectInFlightRef.current = true;
            // collect the bank account by using the Stripe Financial Connections API
            const collectResult = await stripe.collectBankAccountForSetup({
                clientSecret,
                params: {
                    payment_method_type: 'us_bank_account',
                    payment_method_data: {
                        billing_details: {
                            name: holderName || email || 'Account holder',
                            email: email || undefined,
                        },
                    },
                },
                expand: ['payment_method'],
            });

            if (collectResult.error) {
                // After bank OAuth in another tab, the opener's first collect often errors — retry quietly.
                const oauthHandoffPending = !!localStorage.getItem(FC_OAUTH_HANDOFF_KEY);
                const storedSecret = readStoredFcClientSecret();
                if (!fromOauthResume && oauthHandoffPending && storedSecret) {
                    setParentState({ loading: true });
                    window.setTimeout(() => {
                        if (runOauthResumeFromStorageRef.current) {
                            runOauthResumeFromStorageRef.current('post_oauth_collect_error');
                        }
                    }, 0);
                    return;
                }
                if (!fromOauthResume) {
                    alert(collectResult.error.message || 'Bank connection was cancelled.');
                }
                setParentState({ loading: false });
                return;
            }

            const { setupIntent, paymentMethod } = collectResult;

            if (!setupIntent) {
                alert('Bank connection did not complete. Please try again.');
                setParentState({ loading: false });
                return;
            }

            // --- Confirm mandate on client so microdeposit banks land in requires_action (not premature succeeded) ---
            let confirmedSetupIntent = setupIntent;
            if (confirmedSetupIntent.status === 'requires_confirmation') {
                const confirmResult = await stripe.confirmUsBankAccountSetup(clientSecret);
                if (confirmResult.error) {
                    alert(confirmResult.error.message || 'Bank connection did not complete. Please try again.');
                    setParentState({ loading: false });
                    return;
                }
                confirmedSetupIntent = confirmResult.setupIntent;
            }

            const postConfirmPm = paymentMethod
                || (confirmedSetupIntent && confirmedSetupIntent.payment_method);
            const okStatus = FC_POST_COLLECT_STATUSES.includes(confirmedSetupIntent.status);
            if (!okStatus || (!postConfirmPm && confirmedSetupIntent.status !== 'processing')) {
                alert('Bank connection did not complete. Please try again.');
                setParentState({ loading: false });
                return;
            }

            const resolvedSetupIntentId = setupIntentId || confirmedSetupIntent.id;
            // Server persists pm_ + mandate (IP captured server-side on fallback confirm if needed)

            // send the setup intent id to the server to complete the financial connections flow
            const response = await GGApi.bank_fc_complete(resolvedSetupIntentId);
            if (response.success) {
                if (response.requires_verification) {
                    alert('Your bank account was added but needs microdeposit verification. Go to Settings → Payment Methods to enter the verification code from your bank statement.');
                }
                finishInstantBankAdd({
                    response,
                    bankNext,
                    setState: setParentState,
                    cleanupFn: () => {
                        clearHandoffFallbackTimer();
                        cleanupFcOauthStorage(cleanupFcOauth);
                    },
                });
            } else if (response.message === 'A bank account with this information already exists for your use. Please add a different bank account.') {
                alert(response.message);
                setParentState({ loading: false });
            } else {
                alert(response.message || 'The server encountered an error. Please try again.');
                setParentState({ loading: false });
            }
        } catch (err) {
            alert('Something went wrong connecting your bank. Please try again.');
            setParentState({ loading: false });
        } finally {
            collectInFlightRef.current = false;
        }
    }, [stripe, setParentState, save_gift_for_oauth, bankNext, cleanupFcOauth, oauthResumeOnly]);

    const runOauthResumeFromStorage = useCallback((source) => {
        if (resumeStarted.current) {
            return;
        }
        if (collectInFlightRef.current) {
            return;
        }
        const storedSecret = readStoredFcClientSecret();
        const storedId = readStoredFcSetupIntentId();
        if (!storedSecret) {
            return;
        }
        if (!stripe) {
            return;
        }
        resumeStarted.current = true;
        runFcCollection(storedSecret, storedId, { fromOauthResume: true });
    }, [stripe, runFcCollection]);

    runOauthResumeFromStorageRef.current = runOauthResumeFromStorage;

    const handleOpenerHandoff = useCallback((source) => {
        if (handoffDebounceRef.current) {
            return;
        }
        handoffDebounceRef.current = true;
        window.setTimeout(() => {
            handoffDebounceRef.current = false;
        }, 2000);

        setParentState({ loading: true });

        clearHandoffFallbackTimer();

        // Prefer the original collectBankAccountForSetup promise on this tab after popup closes.
        if (collectInFlightRef.current) {
            return;
        }

        handoffFallbackTimerRef.current = window.setTimeout(() => {
            handoffFallbackTimerRef.current = null;
            if (!collectInFlightRef.current && runOauthResumeFromStorageRef.current) {
                runOauthResumeFromStorageRef.current(`${source}_fallback`);
            }
        }, 2000);
    }, [clearHandoffFallbackTimer, setParentState]);

    // Fix A: same-tab OAuth return — wait for Stripe.js before resuming FC.
    useEffect(() => {
        if (!oauthResumeOnly || !fc_client_secret) {
            return;
        }
        runOauthResumeFromStorage('same_tab_resume');
    }, [oauthResumeOnly, fc_client_secret, stripe, runOauthResumeFromStorage]);

    // Fix B: popup/second-tab OAuth — focus opener; only resume if original collect did not finish.
    useEffect(() => {
        if (oauthResumeOnly) {
            return;
        }

        function onStorage(event) {
            if (event.key !== FC_OAUTH_HANDOFF_KEY) {
                return;
            }
            handleOpenerHandoff('opener_storage');
        }

        function onMessage(event) {
            if (event.origin !== window.location.origin) {
                return;
            }
            if (!event.data || event.data.type !== FC_OAUTH_MESSAGE_TYPE) {
                return;
            }
            handleOpenerHandoff('opener_postMessage');
        }

        window.addEventListener('storage', onStorage);
        window.addEventListener('message', onMessage);
        return () => {
            window.removeEventListener('storage', onStorage);
            window.removeEventListener('message', onMessage);
            clearHandoffFallbackTimer();
        };
    }, [oauthResumeOnly, handleOpenerHandoff, clearHandoffFallbackTimer]);

    // handle connect click
    const handleConnectClick = () => {
        setParentState({ verification: 'Instant' });
        runFcCollection(null, null);
    };

    if (oauthResumeOnly) {
        return null;
    }

    if (verification !== 'Instant') {
        return null;
    }

    // render the instant bank fc component
    return (
        <div style={{ marginTop: '16px' }}>
            <p className="tiny_text" style={{ color: '#6b7280' }}>
                {ACH_MANDATE_TEXT}
            </p>
            <MasterButton
                buttonText="Connect bank account"
                handleButtonClick={handleConnectClick}
                notSticky={true}
            />
            <p className="tiny_text" style={{ color: '#6b7280', marginTop: '8px' }}>
                Secure bank sign-in is provided by Stripe Financial Connections.
            </p>
        </div>
    );
}

/** Instant verification tab button (Stripe branding). */
export function InstantBankFcTab({ verification, onSelect }) {
    return (
        <div onClick={onSelect}
            className={verification === 'Instant' ? 'click_button_selected click_button' : 'click_button'}>
            <p className="bold">Instant Verification</p>
            <p className="small_text">Sign in to your bank to instantly verify your checking account</p>
            <div className="powered_by_plaid">
                <p className="tiny_text">Secured by </p>
                <span className="tiny_text bold">Stripe</span>
            </div>
        </div>
    );
}
